/**
 * FRIDGE-1495 — which box a brand logo gets on /brands, from the logo's shape
 * (its file's width over its height, read on the server from
 * lib/brand-logo-sizes.ts and passed down as IndexBrand.logoAspect).
 *
 * Every /brands logo is drawn whole (object-fit: contain), so a box's width caps
 * a wide wordmark's height. The A-Z card's tile holds a 70 x 38 px logo box
 * (84 x 52 tile), 94 x 38 from a 340 px card. On production 10/17/2026 that
 * painted KitchenAid (10:1) 7 px tall at 320 px, with 19 card logos under 14 px
 * at 320 to 414 px and 9 at 1280.
 *
 * A logo takes a full-width row above the card's text wherever the tile would
 * paint it under 14 px tall. The lines keep 3% in hand, because next/image
 * serves a resized copy whose height is rounded to a whole pixel, which can
 * make a mark wider for its height: advwin, 380 x 77 (4.94:1), is served
 * 96 x 19 (5.05:1) and painted 13.9 px tall in the 70 px box.
 *
 *   'row'         6.5:1 and wider (94 / 14 = 6.7, less 3%): the row on every card.
 *   'row-narrow'  4.8:1 up to 6.5:1 (70 / 14 = 5.0, less 3%): the row on a card
 *                 under 340 px, the 108 px tile from 340 px, where it paints
 *                 14 px or more.
 *   'tile'        narrower than 4.8:1, or a logo with no recorded size: the
 *                 tile, as before (square marks, and anything new until its
 *                 size is recorded).
 */
export type LogoShape = "tile" | "row-narrow" | "row"

/** From this width-to-height ratio a logo takes the row on every card. */
export const ROW_FROM_ASPECT = 6.5

/** From this ratio a logo takes the row on a card under 340 px wide. */
export const NARROW_ROW_FROM_ASPECT = 4.8

export function logoShape(aspect: number | null | undefined): LogoShape {
  if (typeof aspect !== "number" || !Number.isFinite(aspect) || aspect <= 0) return "tile"
  if (aspect >= ROW_FROM_ASPECT) return "row"
  if (aspect >= NARROW_ROW_FROM_ASPECT) return "row-narrow"
  return "tile"
}

/**
 * A wordmark the featured bar and the hero pill give a wider logo box: one
 * that takes the row on every card (6.5:1 and wider). In their standard boxes
 * such a mark painted under 11 px tall (production 10/17/2026: KitchenAid
 * 96 x 9.4 in the featured bar at 320 px, 88 x 8.8 in the hero pill on
 * phones), while a mark under 6.5:1 already paints 14 px or more there
 * (Samsung, 6.06:1, 88 x 14.4), so it keeps its box.
 */
export function isWideWordmark(aspect: number | null | undefined): boolean {
  return logoShape(aspect) === "row"
}
